import { AgEditorComponent } from "@bfx/ag-grid-angular";
import {
  AfterViewInit,
  Component,
  ChangeDetectorRef,
  Renderer2,
  ElementRef,
  IterableDiffers,
  ViewEncapsulation,
  SkipSelf,
  Optional,
  Injector,
  ChangeDetectionStrategy,
} from "@angular/core";
import { ObjectUtils } from "@bfx/primeng/utils";
import {
  AUTOCOMPLETE_VALUE_ACCESSOR,
  AUTOCOMPLETE_VIEW_PROVIDERS,
  BfxAutoCompleteComponent,
} from "../../bfx-element-library/bfx-autocomplete.component";
import { DomHandler } from "@bfx/primeng/dom";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { NativeDesktopSupport } from "app/bfx-native-desktop/native-desktop-support";
import { OELoggerService } from "app/order-entry/services/basic/oe-logger.service";
import { OEScreenContainerClickListenerService } from "../../order-entry/services/oe-container-click-listener.service";
import { Subscription } from "rxjs";
import { BfxFieldSelectDirective } from "../../../shared/base-components/bfx-field-select";
import { BfxInputDirective } from "../../../shared/base-components/bfx-input";
import { NativeDesktopDropdown } from "app/bfx-native-desktop/native-desktop-dropdown";

@Component({
  selector: "df-autocomplete",
  templateUrl: "./html/DfAutoComplete.html",
  host: {
    "[class.ui-inputwrapper-filled]": "filled",
    "[class.ui-inputwrapper-focus]": "focus",
  },
  providers: [DomHandler, ObjectUtils, AUTOCOMPLETE_VALUE_ACCESSOR],
  viewProviders: AUTOCOMPLETE_VIEW_PROVIDERS,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DfAutoComplete
  extends BfxAutoCompleteComponent
  implements AgEditorComponent, AfterViewInit
{
  protected fullProvider: Array<any> = [];
  public edWidth: any = "100";
  protected currentData: any;
  protected isRowSelected: boolean;
  protected isComponentLeg: boolean;
  protected legEnabled: boolean;
  protected editingEnabled: boolean = true;
  protected contextParams: any = null;
  protected clickListenerSubscription: Subscription | null = null;
  nativeDropDown: NativeDesktopDropdown;

  constructor(
    injector: Injector,
    inputDirective: BfxInputDirective,
    fieldDirective: BfxFieldSelectDirective,
    el: ElementRef,
    domHandler: DomHandler,
    renderer: Renderer2,
    objectUtils: ObjectUtils,
    cd: ChangeDetectorRef,
    differs: IterableDiffers,
    protected oeLogger: OELoggerService,
    @SkipSelf() @Optional() public clickListener: OEScreenContainerClickListenerService,
  ) {
    super(
      injector,
      inputDirective,
      fieldDirective,
      el,
      domHandler,
      renderer,
      objectUtils,
      cd,
      differs,
      oeLogger,
    );
    this.oeLogger = this.oeLogger.for(__dirname);
    this.field = "label";
    this.checkDirtyBlur = true;
    this.forceSelection = true;
    this.autoHighlight = true;
    this.dropdown = true;
    this.skipInputBlur = true;
    this.scrollHeight = "110px";
    this.appendTo = "body";
    this.size = "sm";
    this.focusInputOnSelect = true;
  }

  get dropdownMenuClasses() {
    return {
      "bfx-dropdown-multi-select-item-container": this.multiple,
      "bfx-dropdown-single-select-item-container": !this.multiple,
      "bfx-dropdown-hidden": !this.panelVisible,
      "bfx-dropdown-lg": this.lg,
      "bfx-dropdown-md": this.md,
      "bfx-dropdown-sm": this.sm,
      "ag-custom-component-popup": true,
      [this.dropdownMenuClass]: this.dropdownMenuClass != null,
    };
  }

  getValue(): any {
    if (this.value) {
      if (this.contextParams.context != null && this.contextParams.context.setEditedValue != null) {
        this.contextParams.context.setEditedValue(this.value, this.contextParams);
      }

      if (
        this.contextParams.context == null ||
        this.contextParams.context.isAutoCompleteValueLabel == null
      ) {
        return this.value.label;
      } else {
        if (this.contextParams.context.isAutoCompleteValueLabel()) {
          return this.value.label;
        } else {
          return this.value.value;
        }
      }
    }

    if (
      typeof this.contextParams.context?.isAutoCompleteOnInvalidReselectOldValue === "function" &&
      this.contextParams.context.isAutoCompleteOnInvalidReselectOldValue()
    ) {
      // return value we were agInit()'d with in case of undefiened selection
      return this.oldValue;
    }
    return "-";
  }

  agInit(params: any): void {
    // Could use a param value to enable native dropdowns but for the moment, we'll always enable
    this.nativeDropDown = new NativeDesktopDropdown(this, this.oeLogger);
    this.nativeDropDown.target = "generic";

    this.isRowSelected = params.node.isSelected();
    this.isComponentLeg = params.node.data.isComponentLeg;
    this.legEnabled = params.node.data.legEnabled;

    this.contextParams = params;

    if (params.context && params.context.getAutoCompleteValues != null) {
      this.fullProvider = params.context.getAutoCompleteValues(params);
    }

    if (params.context != null && params.context.isCellEditingEnabled) {
      this.editingEnabled = params.context.isCellEditingEnabled(params);
    }

    if (params.context != null && params.context.isAutoCompleteSkipSuggestionsSort) {
      this.skipSuggestionsSort = params.context.isAutoCompleteSkipSuggestionsSort(params);
    }

    if (params.context != null && params.context.getAutoCompleteSelectedValue != null) {
      let value: any = params.context.getAutoCompleteSelectedValue(params, this.fullProvider);
      if (value != null) {
        this.autoHighlightIndex = this.fullProvider.indexOf(value);
        this.writeValue(value);
      }
    }

    if (this.isCancelBeforeStart()) return;

    this.oldValue = params.value;

    this.currentData = params.node.data;
    this.edWidth = params.eGridCell.clientWidth;
    this.cd.markForCheck();
  }

  isPopup?(): boolean {
    return true;
  }

  search(event: any, query: string) {
    super.search(event, query);
    if (query != null && query.trim().length > 0) {
      this.suggestions = this.fullProvider.filter(
        (a) => a.label.toLowerCase().indexOf(query.toLowerCase()) >= 0,
      );
    } else {
      this.suggestions = this.fullProvider;
    }
    this.cd.markForCheck();
  }

  isCancelBeforeStart?(): boolean {
    return !this.editingEnabled;
  }

  isCancelAfterEnd?(): boolean {
    return !this.editingEnabled;
  }

  onKeydown(event) {
    super.onKeydown(event);
    if (!this.panelVisible) {
      if (event.which === 40) {
        this.search(event, event.target.value);
      }
    }
  }

  onInputClick(event: MouseEvent) {
    if (this.documentClickListener || this.clickListener != null) {
      this.inputClick = true;
      this.cd.markForCheck();
    }
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    if (this.clickListenerSubscription != null) {
      this.clickListenerSubscription.unsubscribe();
      this.clickListenerSubscription = null;
    }

    this.clickListenerSubscription =
      this.clickListener == null
        ? null
        : this.clickListener.$.subscribe((event: MouseEvent) => {
            if (event.which === 3) {
              return;
            }

            if (!this.inputClick && !this._isDropdownClick(event)) {
              this.hide();
            }

            this.inputClick = false;
            this.markForCheck();
          });

    this.afterViewInitReady.then(() => {
      this.focusInput();

      this.handleDropdownClick(null);
    });
  }

  ngOnDestroy() {
    if (this.clickListenerSubscription != null) {
      this.clickListenerSubscription.unsubscribe();
      this.clickListenerSubscription = null;
    }

    super.ngOnDestroy();

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      this.hide();
    } else {
      setTimeout(this.hide.bind(this), 10);
    }
  }

  unbindDocumentClickListener(): void {
    super.unbindDocumentClickListener();
  }
}
